import React, { FC } from 'react';
import TableRow, { ITableRowAction } from 'FeatureModules/ComponentChain/Components/Table/TableRow';
import { IBaseChain, IChainWithDepth } from 'FeatureModules/ComponentChain/types/chain.types';
import { COLUMNS_COMPONENT_CHAINS, ECOMPONENT_CHAIN_COLUMN_IDS } from '../../utils/headerColumns';
import SortableTableHead from 'Components/TableBase/SortableTableHead';
import { StyledTable, StyledTableBody } from 'Components/TableBase/Table';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';

interface IComponentChainTableProps {
  columnKeys: ECOMPONENT_CHAIN_COLUMN_IDS[];
  hasNextPage?: boolean;
  componentChains: (IBaseChain | IChainWithDepth)[];
  selectedIds?: string[];
  actions?: ITableRowAction[];
  displayTableHeader?: boolean;
  onPageEndReached?: () => void;
  onSelectionChange?: (ids: string[]) => void;
  onRowClick: (id: string) => void;
}

const ComponentChainTable: FC<IComponentChainTableProps> = ({
  columnKeys,
  componentChains,
  hasNextPage,
  selectedIds = [],
  actions,
  displayTableHeader,
  onPageEndReached,
  onSelectionChange,
  onRowClick,
}) => {
  const handleSelect = (componentId: string) => {
    const isItemSelected = selectedIds?.includes(componentId);
    if (!isItemSelected) {
      onSelectionChange?.([...selectedIds, componentId]);
    } else {
      const updatedSelection = selectedIds.filter(document => document !== componentId);
      onSelectionChange?.(updatedSelection);
    }
  };

  const shownColumns = COLUMNS_COMPONENT_CHAINS.filter(({ id }) => columnKeys?.includes(id));

  const tableContent = (
    <StyledTable>
      {displayTableHeader && <SortableTableHead headerColumns={shownColumns} />}
      <StyledTableBody data-testid="component-chain-table">
        {componentChains.map(item => {
          const {
            id,
            chainTasksDetails,
            title,
            image,
            inUse,
            ownedBy,
            involvedPartnerCompanies,
            partnerRequest,
          } = 'chain' in item ? item.chain : item;

          return (
            <TableRow
              id={id}
              key={id}
              columnKeys={columnKeys}
              title={title}
              isIndirectChain={'depth' in item ? item.depth > 1 : undefined}
              isSupplyChain={'depth' in item ? item.depth > 0 : undefined}
              taskDetails={chainTasksDetails}
              image={image}
              onRowClick={() => onRowClick(id)}
              selectedIds={selectedIds}
              actions={actions}
              onSelect={handleSelect}
              inUse={inUse}
              ownedBy={ownedBy}
              isPartOfRequest={!!partnerRequest?.id}
              involvedPartnerCompanies={involvedPartnerCompanies}
            />
          );
        })}
      </StyledTableBody>
    </StyledTable>
  );

  return hasNextPage && onPageEndReached ? (
    <InfiniteScrollWrapper hasMore={hasNextPage} next={onPageEndReached}>
      {tableContent}
    </InfiniteScrollWrapper>
  ) : (
    tableContent
  );
};

export default ComponentChainTable;
